/**
 * Riepilogo settimanale via email — configurazione condivisa (INB-27).
 *
 * Il giorno di invio è una preferenza per-tenant salvata in
 * `configurations.settings`: la scrive il pannello "Notifiche automatiche" del
 * profilo web, la legge il cron dell'API. Chiave e default vivono qui perché
 * una divergenza fra le due superfici non produce alcun errore — l'email
 * semplicemente smette di partire, e nessuno se ne accorge.
 */

/** Chiave in `configurations.settings`: interruttore on/off del riepilogo. */
export const WEEKLY_SUMMARY_ENABLED_KEY = 'emailRiepilogo';

/** Chiave in `configurations.settings`: giorno di invio, ISO 1–7. */
export const WEEKLY_SUMMARY_DAY_KEY = 'emailRiepilogoGiorno';

/** Giorno di invio predefinito quando la preferenza non è mai stata salvata: lunedì. */
export const WEEKLY_SUMMARY_DEFAULT_DAY = 1;

/** Etichette dei giorni indicizzate per giorno ISO (1 = lunedì … 7 = domenica). */
export const WEEKDAY_LABELS_IT: Readonly<Record<number, string>> = {
	1: 'Lunedì',
	2: 'Martedì',
	3: 'Mercoledì',
	4: 'Giovedì',
	5: 'Venerdì',
	6: 'Sabato',
	7: 'Domenica',
};

/**
 * Giorno di invio configurato, normalizzato a un ISO 1–7.
 *
 * `settings` è JSON scritto dal client: un valore assente, non numerico o fuori
 * range non deve far saltare l'invio, quindi ricade sul lunedì.
 */
export function weeklySummaryDay(settings: Record<string, unknown>): number {
	const raw = settings[WEEKLY_SUMMARY_DAY_KEY];
	const day = typeof raw === 'number' ? raw : Number(raw);
	return Number.isInteger(day) && day >= 1 && day <= 7 ? day : WEEKLY_SUMMARY_DEFAULT_DAY;
}

/**
 * Giorno della settimana ISO (1 = lunedì … 7 = domenica) del giorno civile di
 * Roma per l'istante dato.
 *
 * Il cron gira in UTC ma il giorno che l'amministratore ha scelto è quello del
 * suo calendario: alle 07:05 UTC i due coincidono sempre, ma legare il confronto
 * al fuso italiano evita che uno spostamento dell'orario del cron cambi in
 * silenzio il giorno di invio.
 */
export function romeIsoWeekday(now: Date): number {
	const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Europe/Rome',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	})
		.format(now)
		.split('-')
		.map(Number);
	return new Date(Date.UTC(y as number, (m as number) - 1, d as number, 12)).getUTCDay() || 7;
}
